/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import {
	ISchemaConstBoolean,
	ISchemaConstBooleanModel,
	ISchemaConstObjectModel,
	ISchemaValueModel,
	isSchemaConstBoolean,
	isSchemaConstBooleanModel,
	isSchemaValueModel,
	SCHEMA_VALUE_TYPE,
	TGenericModelNode,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import {
	FieldButton,
	FieldGroup,
	FieldSection,
	FieldSectionContent,
	FieldSectionExpander,
	FieldSectionHeader
} from "@adapptio/lib-editor-ui";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { T, useTranslate } from "@adapptio/lib-components";
import { FieldHeaderButton } from "../../Fields/FieldHeaderButton";
import { termsEditorInspector } from "../../../../terms/inspector";
import { InspectModel } from "../InspectModel";

interface IControlConstObject_EditorMetaData {
	showExpanded?: boolean;
}

export const ControlConstObject = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as ISchemaConstObjectModel<TSchemaConstObjectProps>;
	const meta = model.schema.getEditorMetaData<IControlConstObject_EditorMetaData>(model);

	if (model.schema.opts.editorOptions?.controlType === "buttons") {
		if (meta.showExpanded) {
			return {
				headerControls: {
					component: ControlConstObject_ButtonsHeaderControls,
					model: model
				},
				children: {
					component: ControlConstObject_ButtonsChild,
					model: model
				}
			};
		} else {
			return {
				headerControls: {
					component: ControlConstObject_ButtonsHeaderControls,
					model: model
				},
				primary: {
					component: ControlConstObject_ButtonsPrimary,
					model: model
				}
			};
		}
	} else {
		return {
			primaryChildren: {
				component: ControlConstObject_PrimaryChildren,
				model: model
			},
			children: {
				component: ControlConstObject_Children,
				model: model
			},
			displayInSection: true,
			sectionDefaultExpanded: model.schema.opts.editorOptions?.defaultExpanded || false
		};
	}
};

export const ControlConstObject_PrimaryChildren: TInspectorControlComponent<
	ISchemaConstObjectModel<TSchemaConstObjectProps>
> = ({ model: _model, depth, path }) => {
	const { model } = useBlueprintModel(_model, "constObject");

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return useMemo(() => {
		return (
			<>
				{Object.keys(model.props)
					.filter((propKey) => {
						const group = model.schema.opts.props[propKey].group;
						return (
							group?.id === "__primary__" &&
							!model.schema.opts.props[propKey].schema.opts.editorOptions?.hidden
						);
					})
					.sort((aKey, bKey) => {
						const a = model.schema.opts.props[aKey];
						const b = model.schema.opts.props[bKey];

						return a.order > b.order ? 1 : a.order < b.order ? -1 : 0;
					})
					.map((propKey) => {
						return (
							<InspectModel
								key={propKey}
								model={model.props[propKey]}
								depth={depth}
								path={path.concat([ propKey ])}
								fallbackSchemaOpts={{
									label: propKey
								}}
							/>
						);
					})}
			</>
		);
	}, [ model ]);
};

export const ControlConstObject_Children: TInspectorControlComponent<
	ISchemaConstObjectModel<TSchemaConstObjectProps>
> = ({ model: _model, depth, path }) => {
	const { model } = useBlueprintModel(_model, "constObject");

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return useMemo(() => {
		let lastId = 0;

		const groups: {
			[K: string]: {
				id: string;
				label: string;
				description?: string;
				order: number;
				defaultExpanded: boolean;
				items: Array<{
					model: TGenericModelNode;
					order: number;
					key: string;
				}>;
			};
		} = {};

		Object.keys(model.props).forEach((propKey) => {
			const propModel = model.props[propKey];
			const group = model.schema.opts.props[propKey].group;

			if (propModel.schema.opts.editorOptions?.hidden) {
				return;
			}

			if (group) {
				if (group.id === "__primary__") {
					return;
				}

				if (!groups[group.id]) {
					groups[group.id] = {
						id: group.id,
						label: group.label,
						description: group.description,
						order: group.order !== undefined ? group.order : 0,
						defaultExpanded: group.defaultExpanded || false,
						items: []
					};
				}

				groups[group.id].items.push({
					model: propModel,
					order: model.schema.opts.props[propKey].order,
					key: propKey
				});
			} else {
				groups["_" + lastId++] = {
					id: null,
					label: null,
					description: null,
					order: model.schema.opts.props[propKey].order,
					defaultExpanded: true,
					items: [
						{
							model: propModel,
							order: 0,
							key: propKey
						}
					]
				};
			}
		});

		const groupList = Object.keys(groups)
			.map((groupId) => {
				return {
					...groups[groupId],
					items: groups[groupId].items.sort((a, b) =>
						a.order > b.order ? 1 : a.order < b.order ? -1 : 0
					)
				};
			})
			.sort((a, b) => (a.order > b.order ? 1 : a.order < b.order ? -1 : 0));

		return (
			<>
				{groupList.map((group) => {
					if (group.id !== null) {
						return (
							<FieldSection
								key={group.id}
								collapsible
								defaultCollapsed={!(group.defaultExpanded ?? true)}
								stateKey={path.join(".") + group.id}
							>
								<FieldSectionHeader>
									<T domain="editor" term={group.label} />
								</FieldSectionHeader>
								<FieldSectionContent disablePadding={depth <= 1}>
									{group.items.map((item) => {
										return (
											<InspectModel
												key={item.key}
												model={item.model}
												depth={depth + 1}
												path={path.concat([ item.key ])}
												fallbackSchemaOpts={{
													label: item.key
												}}
											/>
										);
									})}
								</FieldSectionContent>
								{depth > 0 ? <FieldSectionExpander /> : null}
							</FieldSection>
						);
					} else {
						return group.items.map((item) => {
							return (
								<InspectModel
									key={item.key}
									model={item.model}
									depth={depth}
									path={path.concat([ item.key ])}
									fallbackSchemaOpts={{
										label: item.key
									}}
								/>
							);
						});
					}
				})}
			</>
		);
	}, [ model ]);
};

export const ControlConstObject_ButtonsHeaderControls: TInspectorControlComponent<
	ISchemaConstObjectModel<TSchemaConstObjectProps>
> = ({ model: _model }) => {
	const { model } = useBlueprintModel(_model, "constObject");

	const toggleShowExpanded = useCallback(() => {
		if (!model) {
			return;
		}

		const prevMeta = model.schema.getEditorMetaData<IControlConstObject_EditorMetaData>(model);
		model.schema.setEditorMetaData<IControlConstObject_EditorMetaData>(model, {
			showExpanded: !prevMeta.showExpanded
		});
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const meta = model.schema.getEditorMetaData<IControlConstObject_EditorMetaData>(model);

	return (
		<FieldHeaderButton icon="mdi/code-braces" isActive={meta.showExpanded} onClick={toggleShowExpanded} />
	);
};

export const ControlConstObject_ButtonsPrimary: TInspectorControlComponent<
	ISchemaConstObjectModel<TSchemaConstObjectProps>
> = ({ model: _model, path }) => {
	const { model } = useBlueprintModel(_model, "constObject");

	return useMemo(() => {
		if (!model) {
			return <InvalidModelNode model={_model} />;
		}

		return (
			<FieldGroup>
				{Object.keys(model.props).map((propKey) => {
					const propModel = model.props[propKey];

					if (
						isSchemaValueModel<ISchemaConstBoolean>(propModel) &&
						isSchemaConstBoolean(propModel.schema.constSchema)
					) {
						return (
							<ControlConstObject_ButtonsPrimary_Value
								key={propKey}
								model={propModel}
								depth={0}
								path={path.concat([ propKey ])}
							/>
						);
					} else if (isSchemaConstBooleanModel(propModel)) {
						return (
							<ControlConstObject_ButtonsPrimary_Const
								key={propKey}
								model={propModel}
								depth={0}
								path={path.concat([ propKey ])}
							/>
						);
					} else {
						// eslint-disable-next-line max-len
						console.warn(
							"Object model has displayStyle property but not all properties are schema of value<constBoolean> or constBoolean."
						);
						return null;
					}
				})}
			</FieldGroup>
		);
	}, [ model, model?.editorMetaData ]);
};

export const ControlConstObject_ButtonsPrimary_Value: TInspectorControlComponent<
	ISchemaValueModel<ISchemaConstBoolean>
> = ({ model: _model }) => {
	const t = useTranslate();
	const { model } = useBlueprintModel(_model, "value");
	const { model: constModel } = useBlueprintModel(model?.constant, "constBoolean");

	const handleClick = useCallback(() => {
		if (!constModel) {
			return;
		}

		constModel.schema.setValue(constModel, !constModel.value, true);
	}, [ constModel ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const constSchema = model.schema.constSchema;

	let tooltip;

	if (model.type !== SCHEMA_VALUE_TYPE.CONST) {
		if (constSchema.opts.icon) {
			tooltip = `${t("editor", constSchema.opts.label)} (${t(
				"editor",
				termsEditorInspector.common.setAsDynamicValue
			)})`;
		} else {
			tooltip = t("editor", termsEditorInspector.common.setAsDynamicValue);
		}
	} else if (constSchema.opts.icon) {
		tooltip = t("editor", constSchema.opts.label);
	}

	return (
		<FieldButton
			icon={constSchema.opts.icon}
			tooltip={tooltip}
			selected={model.constant?.value}
			disabled={model.type !== SCHEMA_VALUE_TYPE.CONST}
			onClick={handleClick}
		>
			{!constSchema.opts.icon ? t("editor", constSchema.opts.label) : null}
		</FieldButton>
	);
};

export const ControlConstObject_ButtonsPrimary_Const: TInspectorControlComponent<ISchemaConstBooleanModel> =
	({ model: _model }) => {
		const t = useTranslate();
		const { model } = useBlueprintModel(_model, "constBoolean");

		const handleClick = useCallback(() => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, !model.value, true);
		}, [ model ]);

		if (!model) {
			return <InvalidModelNode model={_model} />;
		}

		const schema = model.schema;

		return (
			<FieldButton
				icon={schema.opts.icon}
				tooltip={schema.opts.icon ? t("editor", schema.opts.label) : undefined}
				selected={model.value}
				onClick={handleClick}
			>
				{!schema.opts.icon ? t("editor", schema.opts.label) : null}
			</FieldButton>
		);
	};

export const ControlConstObject_ButtonsChild: TInspectorControlComponent<
	ISchemaConstObjectModel<TSchemaConstObjectProps>
> = ({ model: _model, depth, path }) => {
	const { model } = useBlueprintModel(_model, "constObject");

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return useMemo(() => {
		return (
			<FieldSectionContent>
				{Object.keys(model.props).map((propKey) => {
					return (
						<InspectModel
							key={propKey}
							model={model.props[propKey]}
							depth={depth}
							path={path.concat([ propKey ])}
							fallbackSchemaOpts={{
								label: propKey
							}}
						/>
					);
				})}
			</FieldSectionContent>
		);
	}, [ model ]);
};
